import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { getConfig } from '@/config/appConfig';
import { ServiceFout } from '../types';

/**
 * Eén gedeelde verbinding met Supabase.
 *
 * De publieke sleutel hoort in de frontend te staan; de beveiliging zit in de
 * row level security-regels uit supabase/schema.sql. Zonder ingelogde
 * gebruiker geeft de database domweg niets terug.
 */

let client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  const { url, publicKey } = getConfig().supabase;

  if (!url || !publicKey) {
    throw new ServiceFout(
      'Supabase is niet ingesteld. Vul VITE_SUPABASE_URL en VITE_SUPABASE_PUBLIC_KEY in .env in.'
    );
  }

  if (!client) {
    client = createClient(url, publicKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        // De app gebruikt geen magic links; de sessie komt uit localStorage.
        detectSessionInUrl: false,
      },
    });
  }

  return client;
}

/** Alleen nodig als de configuratie tijdens de sessie verandert. */
export function resetSupabase(): void {
  client = null;
}

/** Vertaalt een Supabase-fout naar een leesbare Nederlandse melding. */
export function leesbareFout(fout: { message?: string; code?: string } | null): string {
  if (!fout) return 'Onbekende fout.';
  const bericht = fout.message ?? '';

  if (/Invalid login credentials/i.test(bericht)) {
    return 'E-mailadres of wachtwoord klopt niet.';
  }
  if (/Email not confirmed/i.test(bericht)) {
    return 'Dit account is nog niet bevestigd. Check je mail.';
  }
  if (/Failed to fetch|NetworkError/i.test(bericht)) {
    return 'Geen verbinding met de database.';
  }
  if (fout.code === '42P01') {
    return 'De tabel bestaat nog niet. Draai supabase/schema.sql in de SQL Editor.';
  }
  if (fout.code === '23505') {
    return 'Voor deze woning bestaat al een registratie.';
  }
  if (fout.code === '42501' || /row-level security/i.test(bericht)) {
    return 'Geen rechten. Log opnieuw in.';
  }
  return bericht || 'Onbekende fout.';
}
